import {QueryClient} from '@components/@query';
import {getUrls} from '@config/endpoints';
import {QueryKey} from '@config/queryKeys';
import {httpAuth} from '@http/httpAuth';
import {AuthService} from '@services/auth/auth.service';
import {merge} from '@utils/merge';
import {DeepPartial} from '@utils/typescript';
import {
    ICancelSubscriptionUrlResponse,
    IProfileResponse,
    IUser,
    IUserPaymentDetailsResponse,
    IUserResponse,
} from './user.model';

export class UserService {
    static getCurrentUser = async () => {
        const hasToken = await AuthService.getHasToken();
        if (!hasToken) {
            return null;
        }
        const {data} = await httpAuth.get<IUserResponse>(getUrls().USER);
        return data.user;
    };

    static getProfile = async () => {
        const {data} = await httpAuth.get<IProfileResponse>(
            getUrls().COMMUNITY_PROFILE,
        );
        return data.profile;
    };

    static updateUsername = async (username: string) => {
        await httpAuth.post(getUrls().UPDATE_USERNAME, {
            username,
        });
    };

    static uploadProfileAvatar = async (imageData: Blob) => {
        const formData = new FormData();
        formData.append('avatar', imageData);

        await httpAuth.post(getUrls().COMMUNITY_AVATAR, formData, {
            headers: {},
        });
    };

    static deleteProfileAvatar = async () => {
        await httpAuth.delete(getUrls().COMMUNITY_AVATAR);
    };

    static refreshUser = async () => {
        await QueryClient.invalidateQueries({queryKey: [QueryKey.USER]});
    };

    static updateUser = async (updatedUserData: DeepPartial<IUser>) => {
        const user = QueryClient.getQueryData([QueryKey.USER]);
        const newUser = merge(user, updatedUserData);
        await httpAuth.patch(getUrls().USER, {
            user: updatedUserData,
        });
        QueryClient.setQueryData([QueryKey.USER], () => {
            return newUser;
        });
        await AuthService.refreshSession();
    };

    static updatePassword = async (updatedPassword: {
        newPassword: string;
        currentPassword: string;
    }) => {
        await httpAuth.post(getUrls().USER_UPDATE_PASSWORD, {
            currentPassword: updatedPassword.currentPassword,
            newPassword: updatedPassword.newPassword,
        });
    };

    static getCancelSubscriptionUrl = async () => {
        const {data} = await httpAuth.get<ICancelSubscriptionUrlResponse>(
            getUrls().USER_CANCEL_SUBSCRIPTION,
        );
        return data.cancelUrl;
    };

    static resumeSubscription = async (subscriptionId: string) => {
        const {data} = await httpAuth.patch<ICancelSubscriptionUrlResponse>(
            getUrls().USER_RESUME_SUBSCRIPTION(subscriptionId),
        );
        await UserService.refreshUser();
        return data.cancelUrl;
    };

    static updatePaymentMethodExternal = async () => {
        const {data} = await httpAuth.get<{updatePaymentMethodUrl: string}>(
            getUrls().USER_MANAGE_PAYMENT_METHODS,
        );
        const a = document.createElement('a');
        a.href = data.updatePaymentMethodUrl;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.click();
    };

    static getPaymentDetails = async () => {
        try {
            const res = await httpAuth.get<IUserPaymentDetailsResponse>(
                getUrls().USER_PAYMENT_DETAILS,
            );
            return res.data.paymentDetails;
        } catch (error) {
            return;
        }
    };

    static upgradeSubscription = async () => {
        await httpAuth.put(getUrls().USER_UPGRADE_SUBSCRIPTION);
        await QueryClient.invalidateQueries({queryKey: [QueryKey.USER]});
    };

    static updateTimezone = async (timezone: string) => {
        await httpAuth.post(getUrls().USER_UPDATE_TIMEZONE, {
            timezone,
        });
        QueryClient.setQueryData([QueryKey.USER], (prevData: IUser | null) => {
            if (!prevData) {
                return null;
            }
            return {...prevData, timezone};
        });
    };
}
